/**
 * Validate a `confirmed` pair status BEFORE any surface persists it.
 *
 * ⚠️ WHY THIS IS SHARED, AND WHY THE ASYMMETRY IT CLOSES RAN THE WRONG WAY. Four surfaces
 * complete pairing. `apps/cli` validated all three required fields before writing
 * (`commands/pair.ts`), with reasoning that transfers exactly: *"An empty androidDeviceId
 * would silently produce broken `to=` query params on subsequent dismiss/reply calls; an empty
 * token would yield 401s on every request"*, and the keys are checked *"BEFORE persisting, so a
 * bad server response doesn't leave a corrupt config behind that the user must `unpair` to
 * clear."*
 *
 * `apps/web` and `apps/extension` validated **none** of them. Both did validate the OPTIONAL
 * `serverPublicKey` — so the care was being spent on the key that decrypts public-API
 * notifications while `phonePubKey`, which decrypts every mirrored notification the product
 * exists to deliver, went straight to storage unchecked.
 *
 * That is not a hypothetical difference in style. A malformed `phonePubKey` persists fine and
 * then fails on every single decrypt, presenting as "mirroring is broken" rather than "pairing
 * stored a bad key" — and the only route out is disconnect and re-pair, which is exactly the
 * outcome the CLI's comment says the check exists to prevent.
 *
 * The failure is fail-closed by construction: rejecting a malformed confirmation costs the
 * user one retry of a flow they are already in the middle of, while accepting one costs them a
 * pairing that looks complete and never works.
 */

import type { PairStatusConfirmed } from "@reflecto/core";
import { inspectServerPublicKey } from "../public-api/envelope";

/**
 * Which part of the confirmation was unusable. Distinguished rather than collapsed into one
 * "invalid response" because the surfaces report differently — the CLI prints a specific
 * next step per case, and a browser surface wants a debug event it can tell apart from a
 * generic decrypt failure.
 */
export type PairConfirmationReject =
  | { field: "androidDeviceId"; reason: "missing" }
  | { field: "extensionToken"; reason: "missing" }
  | { field: "phonePubKey"; reason: "missing" | "invalid_base64" | "wrong_byte_length" };

export type PairConfirmationValidation =
  | { ok: true }
  | { ok: false; reject: PairConfirmationReject };

/**
 * True when `value` is a usable identifier.
 *
 * The `typeof` check is not redundant with the type signature. This data crosses the network
 * from a server that could be older, newer, or misbehaving, and every caller reaches here by
 * narrowing a parsed JSON body — so the compiler's belief that these are `string` is exactly
 * the belief being verified. The CLI's original checks were written the same way.
 */
function isUsableId(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/**
 * Validate every field a surface is about to persist.
 *
 * Checks the two identifiers first and the key last, so the cheapest checks fail fastest — but
 * the ORDER IS NOT the contract. Callers must not infer "the ids were fine" from a
 * `phonePubKey` rejection; only the returned field is asserted.
 *
 * `serverPublicKey` is deliberately NOT validated here. It is optional, its rejection is
 * non-fatal (a surface stores the rest of the pairing and logs), and both browser surfaces
 * already inspect it at their own persist sites with their own debug reporting. Folding it in
 * would turn a survivable omission into a rejected pairing.
 */
export function validatePairConfirmation(
  confirmed: Pick<PairStatusConfirmed, "androidDeviceId" | "extensionToken" | "phonePubKey">
): PairConfirmationValidation {
  if (!isUsableId(confirmed.androidDeviceId)) {
    return { ok: false, reject: { field: "androidDeviceId", reason: "missing" } };
  }
  if (!isUsableId(confirmed.extensionToken)) {
    return { ok: false, reject: { field: "extensionToken", reason: "missing" } };
  }
  if (!isUsableId(confirmed.phonePubKey)) {
    return { ok: false, reject: { field: "phonePubKey", reason: "missing" } };
  }
  // Same 32-byte X25519 shape the server key is held to — reused rather than reimplemented,
  // because two copies of "is this a valid pubkey" is how they come to disagree.
  const key = inspectServerPublicKey(confirmed.phonePubKey);
  if (!key.ok) {
    return { ok: false, reject: { field: "phonePubKey", reason: key.reason } };
  }
  return { ok: true };
}

/**
 * One-line summary for a log or debug event.
 *
 * ⚠️ NEVER INCLUDES THE VALUE. A rejected `phonePubKey` is still key material, and a rejected
 * token is a credential; the field name and the reason are the whole diagnostic, and they are
 * enough to tell a typo from a wrong key shape.
 */
export function describePairConfirmationReject(reject: PairConfirmationReject): string {
  return `${reject.field}: ${reject.reason}`;
}
